import Breadcrumbs from '@mui/material/Breadcrumbs';
import MuiLink from '@mui/material/Link';
import { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import getAllRoutes from '../../routes/routes';
import { Icon } from '../display';
import { IconName } from '../display/Icon';
import LinkButton, { LinkButtonProps } from './LinkButton';
import classes from './NavBar.module.scss';

export default function NavBar({ navigation }: { navigation?: Navigation }) {
  const { leftItems, rightItems } = useNavigation(navigation);

  return (
    <div className={classes.root}>
      <Breadcrumbs aria-label="breadcrumb" className={classes.breadcrumbs}>
        {leftItems.map((item) => (
          <Item item={item} key={`${item.route}-${item.title}`} />
        ))}
      </Breadcrumbs>

      {rightItems.length > 0 && (
        <div className={classes.rightItems}>
          {rightItems.map((item) => (
            <Item item={item} key={`${item.route}-${item.title}`} />
          ))}
        </div>
      )}
    </div>
  );
}

function Item({ item }: { item: NavItem }) {
  if (item.button) {
    const rest = typeof item.button === 'boolean' ? {} : item.button;
    return (
      <LinkButton icon={item.icon} to={item.route} {...rest}>
        {item.title}
      </LinkButton>
    );
  }

  if (item.route) {
    return (
      <MuiLink component={Link} to={item.route}>
        {item.icon && <Icon icon={item.icon} />}
        {item.title}
      </MuiLink>
    );
  }

  return (
    <span>
      {item.icon && <Icon icon={item.icon} />}
      {item.title}
    </span>
  );
}

function useNavigation(navigation: Navigation = []): {
  leftItems: NavItem[];
  rightItems: NavItem[];
} {
  const routes = getAllRoutes();
  const location = useLocation();
  const customItems = Array.isArray(navigation) ? navigation : [navigation];

  const items: NavItem[] = [
    {
      route: '/',
      title: 'Pulse',
      icon: 'home',
    },
  ];

  const rootRoutePath = location.pathname.replace(/(\/[\w-]+).*/, '$1');
  const rootRoute = routes.find((r) => r.route === rootRoutePath);
  if (rootRoute) {
    items.push({
      ...rootRoute,
      route: customItems.length === 0 ? '' : rootRoute.route,
    });
  }

  const allItems: NavItem[] = [
    ...items,
    ...customItems
      .filter(Boolean)
      .map((item) => {
        if ((item as NavItem).title) {
          return item as NavItem;
        }

        return { title: item as string };
      })
      .filter((item) => item?.visible !== false),
  ];

  return {
    leftItems: allItems.filter((item) => !item.right),
    rightItems: allItems.filter((item) => item.right),
  };
}

export interface NavItem {
  button?: boolean | LinkButtonProps;
  icon?: IconName;
  right?: boolean;
  route?: string;
  title: ReactNode;
  visible?: boolean;
}

type NavItemShortcut = NavItem | string;
export type Navigation = NavItemShortcut | NavItemShortcut[];
